'use client';

import * as React from 'react';

/**
 * The announce function: given a message, surface it to assistive
 * technology (e.g. spoken via TTS, or pushed to an ARIA live region).
 * The LIBRARY owns the *mechanism* (this context + the primitives that
 * call it) but defaults to a no-op; the APP injects the actual signal
 * via {@link AccessibilityAnnounceProvider}. This dependency inversion
 * keeps the shared theme primitives free of any app-store / module
 * boundary coupling (ADR-057 / ADR-091).
 *
 * The message is the literal string handed to the announcer — drawer
 * primitives announce the event word ('close', 'back', 'save',
 * 'cancel') or the danger-action label — the same short-string form
 * AIM's own `speakAccessibility(message)` call sites use.
 */
export type AnnounceFn = (message: string) => void;

const noopAnnounce: AnnounceFn = () => {
  /* no-op: announcing is inert until an app supplies an announcer */
};

const AccessibilityAnnounceContext =
  React.createContext<AnnounceFn>(noopAnnounce);

export interface AccessibilityAnnounceProviderProps {
  /**
   * The announce implementation supplied by the app. Receives the raw
   * message string. If omitted, the default no-op is used (so wrapping
   * with the provider but passing nothing is still inert).
   */
  announce?: AnnounceFn;
  children: React.ReactNode;
}

/**
 * @useFor wrapping the app root (or a feature subtree) to inject the
 *   accessibility announcer that shared primitives — `DrawerComponent`,
 *   `DrawerFormFooter` — call when an actionable event occurs. Without
 *   this provider those primitives announce through a no-op, so consumers
 *   that supply no announcer (SMS / ONE / Beacon) announce nothing.
 *
 *   The app owns *what* announcing means (TTS gated on a user
 *   preference, an ARIA live region, etc.) and passes it in via
 *   `announce`. A browser-only TTS convenience is available as
 *   {@link createSpeechAnnouncer}.
 *
 *   AIM mounts this provider at its app root with
 *   `createSpeechAnnouncer({ enabled: isAccessibility })`, so the shared
 *   drawer primitives announce through AIM's accessibility-gated TTS
 *   without the lib reaching into app code.
 */
export function AccessibilityAnnounceProvider({
  announce,
  children,
}: AccessibilityAnnounceProviderProps) {
  // Identity-stable default so consumers reading the context don't see a
  // new function each render when no announcer is supplied.
  const value = announce ?? noopAnnounce;
  return (
    <AccessibilityAnnounceContext.Provider value={value}>
      {children}
    </AccessibilityAnnounceContext.Provider>
  );
}

/**
 * @useFor reading the current announce function inside a component so it
 *   can surface an actionable event to assistive technology. Returns the
 *   app-injected announcer, or a no-op when there is no
 *   {@link AccessibilityAnnounceProvider} above — so calling the result
 *   is always safe and never throws.
 *
 *   Used internally by `DrawerComponent` (close / back) and
 *   `DrawerFormFooter` (save / cancel / danger). App code can use it too
 *   to announce its own events through the same channel.
 */
export function useA11yAnnounce(): AnnounceFn {
  return React.useContext(AccessibilityAnnounceContext);
}

/**
 * @useFor building a browser TTS announce function to hand to
 *   {@link AccessibilityAnnounceProvider}. Uses `window.speechSynthesis`
 *   (a platform browser API, so it carries no app coupling), cancels any
 *   in-flight utterance, then speaks the message — mirroring AIM's
 *   `speakAccessibility` behavior. SSR / environments without
 *   `speechSynthesis` get a safe no-op.
 *
 *   This is a convenience for apps; the library itself never calls it.
 *   `enabled` lets the app gate on a user preference (e.g. an
 *   "accessibility announcements" toggle) and is re-read on every call,
 *   so a memoized announcer can close over a ref/getter if the flag
 *   changes at runtime.
 *
 * @example
 *   const announce = useMemo(
 *     () => createSpeechAnnouncer({ enabled: a11yEnabled }),
 *     [a11yEnabled],
 *   );
 *   return (
 *     <AccessibilityAnnounceProvider announce={announce}>
 *       {children}
 *     </AccessibilityAnnounceProvider>
 *   );
 */
export function createSpeechAnnouncer({
  enabled,
}: {
  enabled: boolean;
}): AnnounceFn {
  return (message: string) => {
    if (!enabled) return;
    if (
      typeof window === 'undefined' ||
      typeof window.speechSynthesis === 'undefined' ||
      typeof SpeechSynthesisUtterance === 'undefined'
    ) {
      return;
    }
    // Interrupt any currently-playing utterance, matching AIM behavior.
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(new SpeechSynthesisUtterance(message));
  };
}
